import { Link } from "react-router-dom";
import {
    CupSoda,
    Cookie,
    ArrowRight,
    ShieldCheck,
    Sparkles,
    Building2,
    Clock,
    Salad,
    Truck,
    Snowflake,
    Repeat,
    LineChart,
    CreditCard,
    Coins,
    HardHat,
    GraduationCap,
    Factory,
    Warehouse,
    Briefcase,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { SITE_IMAGES } from "@/lib/siteAssets";

const CATEGORIES = [
    { icon: <CupSoda size={22} />, title: "Cold drinks", body: "Soft drinks, iced coffees, energy drinks and everyday refreshers — always ice-cold thanks to our summer pre-chill." },
    { icon: <Cookie size={22} />, title: "Snacks", body: "Chips, chocolates, biscuits and everyday favourites. Rotated based on what your team actually buys." },
    { icon: <Salad size={22} />, title: "Healthy options", body: "Nuts, jerky, protein bars, and low-sugar / sugar-free options for sites that want a healthier mix." },
    { icon: <Sparkles size={22} />, title: "Combo machines", body: "One machine, snacks + drinks together — perfect for tighter spaces where a full-height combo unit does it all." },
];

const IDEAL_VENUES = [
    { icon: <Briefcase size={18} />, name: "Offices & co-working" },
    { icon: <GraduationCap size={18} />, name: "Schools & training facilities" },
    { icon: <Factory size={18} />, name: "Factories" },
    { icon: <Warehouse size={18} />, name: "Warehouses" },
    { icon: <HardHat size={18} />, name: "Construction sites" },
    { icon: <Building2 size={18} />, name: "Industrial & trade sites" },
];

const FAQS = [
    { q: "Is the vending machine really free?", a: "For eligible Perth workplaces — yes. We cover the machine, installation, restocking, servicing and insurance. You provide floor space and access to a standard power outlet." },
    { q: "What kinds of sites do you install at?", a: "Offices, schools, training facilities, factories, warehouses, construction sites, industrial and trade sites. Basically any workplace with enough staff and daily visitors to keep a machine busy." },
    { q: "How often do you refill?", a: "Weekly. Every machine, every week. This is our biggest point of difference — no month-long gaps between visits like some competitors." },
    { q: "How do you decide what to stock?", a: "We watch the sales data. If a product isn't selling at your site, we swap it out for something we think will. We don't force stock the way manufacturers want us to — we stock what your people actually buy." },
    { q: "Do you offer coffee or hot drinks?", a: "Not at this stage. We're focused on doing snacks and cold drinks exceptionally well rather than being average at everything." },
    { q: "What payment methods do you support?", a: "All machines are cashless-ready — tap, card, Apple Pay, Google Pay. Food & drink machines can also accept notes and coins if you prefer." },
    { q: "What about Perth summers?", a: "For summer we pre-chill enough stock so the first two drinks in every drink row are cold from the moment we leave. No four-hour wait for an icy Coke." },
    { q: "How quickly can you install?", a: "For approved sites, from agreement to a live, stocked machine is usually within 4 weeks." },
];

export default function FoodDrinkVendingPage() {
    useSeo({
        title: "Free Vending Machine Placement Perth — Snacks & Drinks",
        description:
            "Free vending machine placement (vending machine hire) for Perth workplaces — offices, schools, factories, warehouses and construction sites. Weekly restocks, real product monitoring, summer pre-chill. Cashless or cash payments.",
        canonicalPath: "/food-drink-vending",
        keywords:
            "free vending machine Perth, vending machine hire Perth, free vending machine hire Perth, vending machines Perth, office vending machines Perth, factory vending Perth, warehouse vending Perth, construction site vending Perth, snack vending Perth, drink vending Perth, combo vending machines Perth",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    return (
        <>
            {/* HERO */}
            <section className="pt-12 lg:pt-20 pb-14 lg:pb-24 border-b border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Free vending machine placement — Perth
                        </span>
                        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]" data-testid="fdv-hero-title">
                            The vending<br />machine your team<br />
                            <span className="text-green-600">actually uses.</span>
                        </h1>
                        <p className="mt-5 text-lg text-neutral-600 leading-relaxed max-w-xl">
                            Free snack & drink vending machines for Perth
                            offices, schools, factories, warehouses and
                            construction sites. Weekly restocks, real product
                            monitoring, and pre-chilled drinks in summer — so
                            an ice-cold Coke is <em>always</em> ready.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/host-a-machine" className="vg-pill vg-pill-primary" data-testid="fdv-cta-host">
                                Get a free machine
                                <ArrowRight size={14} />
                            </Link>
                            <a href="#usps" className="vg-pill vg-pill-outline">
                                See what makes us different
                            </a>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-white border border-neutral-200">
                            <img
                                src={SITE_IMAGES.rvmComboTall}
                                alt="Royal Vending combo snack and drink vending machine — Vendagain fleet"
                                className="absolute inset-0 w-full h-full object-contain p-4"
                                loading="eager"
                                data-testid="fdv-hero-image"
                            />
                            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/70 backdrop-blur text-green-400 text-[11px] font-semibold uppercase tracking-widest">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                Combo unit — snacks + drinks
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* USPs — the big differentiators */}
            <section id="usps" className="py-16 lg:py-24 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Our difference
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Four things other Perth vending operators don't do.
                        </h2>
                        <p className="mt-3 text-neutral-600 leading-relaxed">
                            Getting a vending machine is easy. Getting one that
                            actually works for your workplace is harder. This is
                            where we've built our reputation.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <UspCard
                            icon={<Repeat size={22} />}
                            title="Weekly fills. Every machine. Every week."
                            body="No months between visits. We turn up, top up, check dates and go — reliably, week in and week out. If your team drinks a machine dry on a Thursday, they're not thirsty on Friday."
                        />
                        <UspCard
                            icon={<LineChart size={22} />}
                            title="Product monitoring — powered by real data"
                            body="We watch what's selling at your site. Products that aren't moving get pulled and replaced with something we think will land better. We don't force manufacturer stock down anyone's throat — we stock what your people actually buy."
                        />
                        <UspCard
                            icon={<Snowflake size={22} />}
                            title="Summer pre-chill — no waiting for cold Coke"
                            body="Perth summers are brutal. For every drink row, we pre-chill enough stock so the first two units in the row are icy cold the moment we leave. No four-hour wait after a restock. Just tap → cold drink."
                            highlight
                        />
                        <UspCard
                            icon={<Sparkles size={22} />}
                            title="Machines that don't feel like an afterthought"
                            body="We use Royal Vending Machines — clean, modern equipment that fits any professional space and works reliably. Cashless payments are standard; notes and coins available if you want them."
                        />
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            What we vend
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Curated for your team.
                        </h2>
                        <p className="mt-3 text-neutral-600 leading-relaxed">
                            The product mix at every site is tailored to the
                            venue — a warehouse gets electrolytes and hearty
                            snacks; an office gets healthier options; a school
                            gets appropriate items and portion sizes.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {CATEGORIES.map((c) => (
                            <div
                                key={c.title}
                                className="bg-white border border-neutral-200 rounded-2xl p-6 hover:border-neutral-900 transition-colors"
                            >
                                <div className="w-11 h-11 rounded-lg bg-green-100 text-green-700 flex items-center justify-center mb-4">
                                    {c.icon}
                                </div>
                                <h3
                                    className="text-lg font-semibold mb-2"
                                    dangerouslySetInnerHTML={{ __html: c.title }}
                                />
                                <p
                                    className="text-sm text-neutral-600 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: c.body }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* IDEAL VENUES */}
            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-10">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Ideal for
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Where Vendagain machines thrive.
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {IDEAL_VENUES.map((v) => (
                            <div
                                key={v.name}
                                className="bg-white border border-neutral-200 rounded-2xl px-5 py-5 flex items-center gap-3 text-sm text-neutral-900 font-medium hover:border-neutral-900 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-md bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                                    {v.icon}
                                </div>
                                <span dangerouslySetInnerHTML={{ __html: v.name }} />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PAYMENT + SERVICE */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Modern, reliable service
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                            Pay how you want. We do the rest.
                        </h2>
                        <p className="mt-4 text-neutral-600 leading-relaxed">
                            Every food & drink machine we install is
                            cashless-ready as standard, and can be configured to
                            also accept notes and coins if that suits your team.
                            We handle stock, service and repairs — you never
                            have to think about it.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <PerkCard icon={<CreditCard size={18} />} title="Cashless" body="Tap, card, Apple Pay, Google Pay — sorted." />
                        <PerkCard icon={<Coins size={18} />} title="Notes & coins" body="Optional for sites where cash is preferred." />
                        <PerkCard icon={<Truck size={18} />} title="Weekly service" body="Restock, clean and check every 7 days." />
                        <PerkCard icon={<ShieldCheck size={18} />} title="Fully insured" body="Machine and public liability covered — no risk to you." />
                        <PerkCard icon={<Clock size={18} />} title="24/7 access" body="Your team gets refreshments whenever they need them." />
                        <PerkCard icon={<Sparkles size={18} />} title="Clean & modern" body="Machines that fit a professional space, not tuck-away units." />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 lg:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="mb-10 text-center">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            FAQ
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Questions from Perth businesses
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                className="group bg-white border border-neutral-200 rounded-2xl p-5 [&_summary::-webkit-details-marker]:hidden"
                                data-testid={`fdv-faq-${i}`}
                            >
                                <summary className="cursor-pointer flex items-start justify-between gap-3 text-neutral-900 font-medium text-[15px]">
                                    <span>{f.q}</span>
                                    <span className="text-green-500 text-2xl leading-none group-open:rotate-45 transition-transform">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-3 text-neutral-600 text-sm leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 lg:py-20 bg-neutral-950 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                        Want one at your workplace?
                    </h2>
                    <p className="mt-4 text-neutral-300 max-w-lg mx-auto">
                        Free install, no ongoing cost, weekly restocks. It
                        takes less than a minute to enquire.
                    </p>
                    <div className="mt-8">
                        <Link to="/host-a-machine#form" className="vg-pill vg-pill-accent">
                            Register your interest
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

function UspCard({ icon, title, body, highlight }) {
    return (
        <div
            className={`rounded-2xl p-6 h-full border transition-colors ${
                highlight
                    ? "bg-green-500 text-white border-green-500"
                    : "bg-white border-neutral-200 hover:border-neutral-950"
            }`}
        >
            <div
                className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${
                    highlight
                        ? "bg-white/20 text-white"
                        : "bg-neutral-950 text-white"
                }`}
            >
                {icon}
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${highlight ? "text-white" : "text-neutral-950"}`}>
                {title}
            </h3>
            <p
                className={`text-sm leading-relaxed ${highlight ? "text-white/90" : "text-neutral-600"}`}
            >
                {body}
            </p>
        </div>
    );
}

function PerkCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5">
            <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-md bg-green-500 text-white flex items-center justify-center">
                    {icon}
                </div>
                <h3
                    className="text-sm font-semibold"
                    dangerouslySetInnerHTML={{ __html: title }}
                />
            </div>
            <p
                className="text-xs text-neutral-600 leading-relaxed pl-11"
                dangerouslySetInnerHTML={{ __html: body }}
            />
        </div>
    );
}
